import { Injectable, computed, inject, signal } from "@angular/core";
import { combineLatest, mergeMap, skip, switchMap, tap } from "rxjs";
import { FEATURE_FLAGS } from "../app.constants";
import { Subject } from "../_objects/subject";
import { SubjectModelListItem } from "../_objects/subject-model";
import { FeatureFlagService } from "./feature-flag/feature-flag.service";
import { SubjectModelService } from "./http-api/subject-model.service";
import { SubjectService } from "./http-api/subject.service";
import { NetworkStatusService } from "./network-status.service";

type LiveAssetsState = {
  liveAssets: Subject[];
  rootLiveAssets: Subject[];
  assetTypes: SubjectModelListItem[];
};

@Injectable({ providedIn: "root" })
class LiveAssetsStateService {
  private subjectApiService = inject(SubjectService);
  private subjectModelApiService = inject(SubjectModelService);
  private networkStatusService = inject(NetworkStatusService);
  private featureFlagService = inject(FeatureFlagService);
  private rootOnlyToggleEnabled = this.featureFlagService.isFlagEnabled(FEATURE_FLAGS.INSPECTION_ROOT_ONLY_ASSETS);
  private state = signal<LiveAssetsState>({
    liveAssets: [],
    rootLiveAssets: [],
    assetTypes: []
  });

  public liveAssets = computed(() => this.state()?.liveAssets);
  public rootLiveAssets = computed(() => this.state()?.rootLiveAssets);
  public assetTypes = computed(() => this.state()?.assetTypes);

  constructor() {
    this.networkStatusService.applicationMode$
      .pipe(
        skip(1),
        switchMap(() => this.init())
      )
      .subscribe();
  }

  public init() {
    if (this.rootOnlyToggleEnabled()) {
      return combineLatest({
        liveAssets: this.subjectApiService.getSubjects(null, true),
        rootLiveAssets: this.subjectApiService.getSubjects(null, true, true),
        assetTypes: this.subjectModelApiService.getSubjectModels()
      }).pipe(
        tap(({ liveAssets, rootLiveAssets, assetTypes }) => {
          this.state.update(() => ({
            liveAssets,
            rootLiveAssets,
            assetTypes
          }));
        })
      );
    }

    return combineLatest({
      rootLiveAssets: this.subjectApiService.getSubjects(null, true, true),
      assetTypes: this.subjectModelApiService.getSubjectModels()
    }).pipe(
      tap(({ rootLiveAssets, assetTypes }) => {
        this.state.update(() => ({
          liveAssets: rootLiveAssets,
          rootLiveAssets,
          assetTypes
        }));
      })
    );
  }
}

export default LiveAssetsStateService;
